/**
 * HotReloadManager — Central hub for development-time hot-reload.
 *
 * Register watchers by kind (shader | script | asset), debounce rapid
 * saves, and dispatch typed events. Must be explicitly enabled; disabled
 * in production builds.
 *
 * Usage:
 *   app.hotReload?.register('shader', 'src/shaders/sky.ts', handler);
 *   app.hotReload?.enable();
 */

import { Logger } from '../../core/Logger.js';

export type ReloadKind = 'shader' | 'script' | 'asset';

export type ReloadHandler = (path: string, kind: ReloadKind) => void | Promise<void>;

export interface ReloadHandle {
  readonly id: number;
  readonly kind: ReloadKind;
  readonly path: string;
  unregister(): void;
}

export type HotReloadEventMap = {
  'reload:shader': { path: string };
  'reload:script': { path: string };
  'reload:asset': { path: string };
  'reload:error': { path: string; kind: ReloadKind; error: unknown };
  'reload:complete': { path: string; kind: ReloadKind; durationMs: number };
};

type HotReloadEventHandler<K extends keyof HotReloadEventMap> = (
  data: HotReloadEventMap[K],
) => void;

interface WatchEntry {
  id: number;
  kind: ReloadKind;
  path: string;
  handler: ReloadHandler;
}

export class HotReloadManager {
  private static _idCounter = 0;

  private _enabled = false;
  private _entries = new Map<number, WatchEntry>();
  private _pathIndex = new Map<string, Set<number>>();
  private _debounceMs: number;
  private _pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

  /** Per-event typed listeners */
  private _listeners = new Map<string, Array<(data: unknown) => void>>();

  constructor(debounceMs = 200) {
    this._debounceMs = debounceMs;
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Enable the hot-reload manager. No-op if already enabled.
   */
  enable(): void {
    if (this._enabled) return;
    this._enabled = true;
    Logger.info('HotReload', 'Enabled');
  }

  /**
   * Disable the hot-reload manager and cancel pending debounce timers.
   */
  disable(): void {
    if (!this._enabled) return;
    this._enabled = false;
    for (const timer of this._pendingTimers.values()) {
      clearTimeout(timer);
    }
    this._pendingTimers.clear();
    Logger.info('HotReload', 'Disabled');
  }

  get isEnabled(): boolean {
    return this._enabled;
  }

  // ─── Registration ───────────────────────────────────────────────────────────

  /**
   * Register a handler for a specific file path + kind.
   * Returns a handle that can be used to unregister.
   */
  register(kind: ReloadKind, path: string, handler: ReloadHandler): ReloadHandle {
    const id = ++HotReloadManager._idCounter;
    const entry: WatchEntry = { id, kind, path, handler };
    this._entries.set(id, entry);

    let pathSet = this._pathIndex.get(path);
    if (!pathSet) {
      pathSet = new Set();
      this._pathIndex.set(path, pathSet);
    }
    pathSet.add(id);

    const unregister = () => this.unregister(id);
    return { id, kind, path, unregister };
  }

  /**
   * Unregister a handler by its handle id.
   */
  unregister(id: number): void {
    const entry = this._entries.get(id);
    if (!entry) return;

    this._entries.delete(id);

    const pathSet = this._pathIndex.get(entry.path);
    if (pathSet) {
      pathSet.delete(id);
      if (pathSet.size === 0) {
        this._pathIndex.delete(entry.path);
      }
    }
  }

  // ─── Triggering ─────────────────────────────────────────────────────────────

  /**
   * Trigger a reload for the given path, debounced by `debounceMs`.
   * Can be called manually or from adapters (Vite HMR, chokidar, etc.).
   */
  trigger(path: string): void {
    if (!this._enabled) return;

    // Cancel any existing debounce for this path
    const existing = this._pendingTimers.get(path);
    if (existing !== undefined) {
      clearTimeout(existing);
    }

    const timer = setTimeout(() => {
      this._pendingTimers.delete(path);
      void this._doReload(path);
    }, this._debounceMs);

    this._pendingTimers.set(path, timer);
  }

  /**
   * Trigger a reload immediately, bypassing debounce.
   * Useful for programmatic reloads in tests.
   */
  async triggerNow(path: string): Promise<void> {
    if (!this._enabled) return;

    const existing = this._pendingTimers.get(path);
    if (existing !== undefined) {
      clearTimeout(existing);
      this._pendingTimers.delete(path);
    }

    await this._doReload(path);
  }

  // ─── Event emitter ──────────────────────────────────────────────────────────

  on<K extends keyof HotReloadEventMap>(
    event: K,
    handler: HotReloadEventHandler<K>,
  ): () => void {
    const key = event as string;
    let list = this._listeners.get(key);
    if (!list) {
      list = [];
      this._listeners.set(key, list);
    }
    list.push(handler as (data: unknown) => void);

    return () => {
      const arr = this._listeners.get(key);
      if (arr) {
        const idx = arr.indexOf(handler as (data: unknown) => void);
        if (idx !== -1) arr.splice(idx, 1);
      }
    };
  }

  private _emit<K extends keyof HotReloadEventMap>(
    event: K,
    data: HotReloadEventMap[K],
  ): void {
    const list = this._listeners.get(event as string);
    if (!list || list.length === 0) return;
    const snapshot = list.slice();
    for (const fn of snapshot) {
      try {
        fn(data);
      } catch (err) {
        Logger.error('HotReload', `Event handler error on "${event as string}": ${err}`);
      }
    }
  }

  // ─── Internal ───────────────────────────────────────────────────────────────

  private async _doReload(path: string): Promise<void> {
    const live = this._pathIndex.get(path);
    if (!live || live.size === 0) return;

    // Snapshot the Set before iterating across `await` boundaries. A handler
    // that calls `unregister()` during its own async work would otherwise
    // mutate the live Set and silently skip not-yet-visited siblings on the
    // same path. The `_entries.get(id)` check handles the "handler removed
    // during a prior await" case explicitly so we don't run a torn entry.
    const ids = [...live];

    for (const id of ids) {
      const entry = this._entries.get(id);
      if (!entry) continue;

      const t0 = performance.now();
      try {
        await entry.handler(path, entry.kind);

        const durationMs = performance.now() - t0;

        // Emit kind-specific event (type-safe switch avoids index expression issues)
        switch (entry.kind) {
          case 'shader': this._emit('reload:shader', { path }); break;
          case 'script': this._emit('reload:script', { path }); break;
          case 'asset':  this._emit('reload:asset',  { path }); break;
        }

        this._emit('reload:complete', { path, kind: entry.kind, durationMs });
        Logger.info('HotReload', `Reloaded ${entry.kind}: ${path} (${durationMs.toFixed(1)}ms)`);
      } catch (err) {
        Logger.error('HotReload', `Reload failed for ${path}: ${err}`);
        this._emit('reload:error', { path, kind: entry.kind, error: err });
        // Keep old version running — do not rethrow
      }
    }
  }

  // ─── Stats ──────────────────────────────────────────────────────────────────

  get watchedPathCount(): number {
    return this._pathIndex.size;
  }

  get registeredHandlerCount(): number {
    return this._entries.size;
  }

  get pendingReloadCount(): number {
    return this._pendingTimers.size;
  }

  dispose(): void {
    this.disable();
    this._entries.clear();
    this._pathIndex.clear();
    this._listeners.clear();
    Logger.info('HotReload', 'Disposed');
  }
}
